import { Link } from '@mui/material';
import Typography from '@mui/material/Typography/Typography';
import { useGetKeycloakLoginUrlQuery } from '@services/keycloak';
import { pathToVP } from '@utils/route';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { AuthenticationSuccess } from 'src/routes/public/Authentication/AuthenticationSuccess';

const MigrationSuccess = () => {
    const { locale } = useIntl();
    const { state } = useLocation();
    const { required: userRoleRequires2FA, email: userEmail } = (
        state as
            | {
                  twoFAData?: {
                      required: boolean;
                      email: string;
                  };
              }
            | undefined
    )?.twoFAData || { required: false, email: '' };

    const { data: loginUrl, isLoading: isLoadingLoginUrl } = useGetKeycloakLoginUrlQuery(
        { email: userEmail, locale },
        { skip: !userRoleRequires2FA }
    );
    const [searchParams] = useSearchParams();
    const nextParam = searchParams.get('next');

    const continueToProfUrl = useMemo(() => {
        const newParams = new URLSearchParams(searchParams);

        if (!nextParam || (nextParam && !nextParam.startsWith('/app'))) {
            return pathToVP(
                '',
                Array.from(searchParams.entries()).map(([name, value]) => ({ name, value }))
            ).toString();
        } else {
            newParams.delete('next');
            return `${nextParam}?${newParams.toString()}`;
        }
    }, [searchParams]);

    return (
        <>
            <title>Victron Professional - Migration Success</title>
            <AuthenticationSuccess
                title={
                    <FormattedMessage
                        id="auth.migrationSuccess.title"
                        defaultMessage="{brandName} Account Migrated Successfully"
                        description="Success title after account migration. Used in: /auth/migration-success"
                        values={{ brandName: 'Professional' }}
                    />
                }
                message={
                    <>
                        <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                            <FormattedMessage
                                id="auth.migrationSuccess.migrationComplete"
                                defaultMessage="Your {professionalBrand} account has been successfully migrated to {oneIdBrand}."
                                description="Confirmation that account migration was successful. Used in: /auth/migration-success"
                                values={{
                                    professionalBrand: 'Professional',
                                    oneIdBrand: (
                                        <Typography component="span" fontWeight={700} fontSize={14}>
                                            Victron OneID
                                        </Typography>
                                    ),
                                }}
                            />
                        </Typography>
                        <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={2}>
                            <FormattedMessage
                                id="auth.migrationSuccess.unifiedLogin"
                                defaultMessage="This means that you can use the same login for {professionalBrand} and the {vrmPortalLink}."
                                description="Explanation of unified login benefits after migration. Used in: /auth/migration-success"
                                values={{
                                    professionalBrand: 'Victron Professional',
                                    vrmPortalLink: (
                                        <Link
                                            href="https://vrm.victronenergy.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="cursor-pointer"
                                        >
                                            VRM Portal
                                        </Link>
                                    ),
                                }}
                            />
                        </Typography>
                        {userRoleRequires2FA ? (
                            <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={2}>
                                <FormattedMessage
                                    id="auth.migrationSuccess.twoFactorNotice"
                                    defaultMessage="Due to your role, 2FA setup will be mandatory. You can skip the setup for now, but it will be mandatory the next time you sign in."
                                    description="Notice about mandatory 2FA for certain roles. Used in: /auth/migration-success"
                                />
                            </Typography>
                        ) : null}
                    </>
                }
                primaryButtonProps={
                    userRoleRequires2FA
                        ? {
                              href: loginUrl?.login_url ?? '',
                              children: (
                                  <FormattedMessage
                                      id="auth.migrationSuccess.setupTwoFactor"
                                      defaultMessage="Setup 2FA"
                                      description="Text for setup 2FA button. Used in: /auth/setup-2fa and /auth/migration-success"
                                  />
                              ),
                              loading: isLoadingLoginUrl,
                          }
                        : {
                              ...(nextParam && nextParam.startsWith('/app')
                                  ? { to: continueToProfUrl, component: RouterLink }
                                  : { href: continueToProfUrl }),
                              children: (
                                  <FormattedMessage
                                      id="common.continue"
                                      defaultMessage="Continue"
                                      description="Text for continue button"
                                  />
                              ),
                          }
                }
                secondaryButtonProps={
                    userRoleRequires2FA
                        ? {
                              ...(nextParam && nextParam.startsWith('/app')
                                  ? { to: continueToProfUrl, component: RouterLink }
                                  : { href: continueToProfUrl }),
                              children: (
                                  <FormattedMessage
                                      id="auth.migrationSuccess.skipTwoFactor"
                                      defaultMessage="Skip 2FA Setup"
                                      description="Text for skip 2FA setup button. Used in: /auth/migration-success"
                                  />
                              ),
                          }
                        : undefined
                }
                stateVariable="migration-allow"
            />
        </>
    );
};

export { MigrationSuccess };
